import React from 'react';
import { createContext, useState, useEffect } from 'react';
import uiGuidedSteps from '../uiGuidedSteps.json';
import Joyride, { STATUS } from 'react-joyride';
import useApi from '../hooks/useApi';

const TourContext = createContext();

const waitForSelectors = (selectors, { timeout = 500 } = {}) => {
    // eslint-disable-next-line no-undef
    return new Promise((resolve) => {
        // eslint-disable-next-line no-undef
        const remaining = new Set(selectors.filter(Boolean));
        // eslint-disable-next-line no-undef
        const found = new Set();

        remaining.forEach((sel) => {
            if (document.querySelector(sel)) {
                found.add(sel);
                remaining.delete(sel);
            }
        });

        if (remaining.size === 0) {
            resolve({ found: Array.from(found), missing: [] });
            return;
        }

        const observer = new MutationObserver(() => {
            remaining.forEach((sel) => {
                if (document.querySelector(sel)) {
                    found.add(sel);
                    remaining.delete(sel);
                }
            });

            if (remaining.size === 0) {
                observer.disconnect();
                resolve({ found: Array.from(found), missing: [] });
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        setTimeout(() => {
            observer.disconnect();
            resolve({ found: Array.from(found), missing: Array.from(remaining) });
        }, timeout);
    });
};

const TourProvider = ({ children }) => {
    const [runTour, setRunTour] = useState(false);
    const [steps, setSteps] = useState([]);
    const [tourStatus, setTourStatus] = useState('');
    const { handlePut } = useApi();
    const [tourPath, setTourPath] = useState(null);

    const handleCompleteTour = async (tourName) => {
        if (tourName) {
            await handlePut('user/tour', { tourName });
        }
    };

    const handleRunTour = async ({ path, timeout = 500 } = {}) => {
        setTourPath(path);

        const config = uiGuidedSteps[path];

        if (!config || !Array.isArray(config)) {
            setSteps([]);
            setRunTour(false);
            setTourPath(null);
            return;
        }

        const selectors = config.map((s) => s.target);
        const { found } = await waitForSelectors(selectors, { timeout });

        const resolvedSteps = config
            .filter((s) => found.includes(s.target))
            .map((step, index) => ({
                ...step,
                disableBeacon: index === 0
            }));

        if (resolvedSteps.length === 0) {
            setSteps([]);
            setRunTour(false);
            setTourPath(null);
            return;
        }

        setSteps(resolvedSteps);
        setRunTour(true);
    };

    const handleTourCallback = (data) => {
        const { status, action } = data;

        if (action === 'close') {
            setRunTour(false);
            setTourPath(null);
            return;
        }

        if (status === STATUS.SKIPPED) {
            return;
        }

        setTourStatus(status);
    };

    useEffect(() => {
        if (tourStatus === STATUS.FINISHED) {
            setRunTour(false);
            handleCompleteTour(tourPath);
            setTourPath(null);
        }
    }, [tourStatus]);

    return (
        <TourContext.Provider value={{
            handleCompleteTour,
            handleRunTour
        }}>
            <Joyride
                steps={steps}
                run={runTour}
                callback={handleTourCallback}
                continuous
                scrollToFirstStep
                showProgress
                disableCloseOnEsc
                disableOverlayClose
                locale={{ last: 'Complete', open: 'Continue UI Tour' }}
                styles={{
                    options: {
                        zIndex: 10000,
                        beaconSize: 36
                    },
                    tooltipContent: {
                        textAlign: 'left',
                        paddingBottom: '0px',
                        paddingLeft: '0px',
                        paddingRight: '0px'
                    },
                    tooltipTitle: {
                        textAlign: 'left',
                        marginTop: '10px',
                        paddingBottom: '0px'
                    }
                }}
            />
            {children}
        </TourContext.Provider>
    );
};

export { TourProvider, TourContext };